/**
 * 漫剧题材分类法 · 前后端共用枚举 v3
 * 11 大漫剧热门题材 · 覆盖红果热门 / 穿越爽文 / 维度差 / 晋江 / 耽美 / 穿书系统 / 基建降维 / 中式恐怖 / 规则怪谈 / 都市暗网 / 冷感生存日志
 */

/** 11 大题材 key */
export const GENRES = [
  '红果热门',
  '传统穿越爽文',
  '维度差穿越',
  '晋江高榜风格',
  '耽美漫剧',
  '穿书系统爽文',
  '基建科技降维',
  '中式恐怖',
  '规则怪谈',
  '都市暗网',
  '冷感生存日志',
  '宇宙进化爽文',
] as const;

export type Genre = (typeof GENRES)[number];

/** 每个题材的完整元数据（用于 UI 展示 + AI 创作辅助） */
export interface GenreMeta {
  key: Genre;
  label: string;
  tagline: string;
  description: string;
  coreGimmick: string;       // 核心金手指/套路
  typicalSettings: string[]; // 典型背景设定
  audienceTags: string[];    // 主要受众
  emotionTags: string[];     // 核心情绪
  exampleTitles: string[];   // 参考作品/设定
  hotPoints: string[];       // 爽点关键词
}

export const GENRE_META: GenreMeta[] = [
  {
    key: '红果热门',
    label: '红果热门',
    tagline: '仿造红果当前最火爆款，踩中平台用户最买单的爽点组合',
    description:
      '基于红果(番茄)短剧平台当前最热门的 AI 短剧做灵感抽取，抽取爆款基因生成同类高传播潜力剧情。红果当前核心赛道不只限豪门马甲，还覆盖年代家庭、县城职业、萌宝读心、系统生存反哺、轻奇幻民俗等下沉高转化玩法。92% 爆款为女频，默认女频创作；当前爆款共性是 2-3 个热门元素融合（如县城职业+打脸逆袭、年代家庭+她力量、系统+荒年反哺、读心+亲情修罗场）。本题材走标准红果模式：台词驱动、短平快、高反转密度、强开场钩子。',
    coreGimmick: '抽取红果当前爆款基因，2-3 个热门元素融合，逆袭打脸为核、强钩子高反转',
    typicalSettings: ['现代都市', '豪门商战', '年代家庭', '穿越/系统融合世界', '古风权谋'],
    audienceTags: ['逆袭打脸', '下沉甜宠', '都市职场'],
    emotionTags: ['爽', '解气', '甜'],
    exampleTitles: ['十八岁太奶奶驾到', '霍总夫人马甲又掉了', '家里家外', '荒年全村啃树皮我有系统满仓肉'],
    hotPoints: ['当众打脸', '系统逆袭', '读心揭秘', '绝地翻盘', '大女主崛起', '年代亲情反杀', '县城职业翻盘', '银发掌家'],
  },
  {
    key: '传统穿越爽文',
    label: '传统穿越爽文',
    tagline: '现代人带着记忆/技能穿越，利用信息差碾压土著',
    description:
      '现代人带着记忆、技能或现代意识穿越到古代/异世界，利用现代科技、商业思维、历史知识、政治远见制造"降维打击"。节奏快、打脸爽、逆袭感强，常与权谋/基建/商战结合。',
    coreGimmick: '现代知识/技能信息差，开局即王炸',
    typicalSettings: ['古代朝堂', '异世界王朝', '古代商界', '乱世军阀'],
    audienceTags: ['都市职场', '逆袭打脸', '下沉甜宠'],
    emotionTags: ['爽', '燃', '甜'],
    exampleTitles: ['商业制霸型', '古代建工业型', '穿到乱世当皇帝'],
    hotPoints: ['打脸逆袭', '权谋碾压', '商战称霸', '知识降维'],
  },
  {
    key: '维度差穿越',
    label: '维度差穿越',
    tagline: '反套路+趣味反差，高度现代的物品/知识直接植入古代',
    description:
      '将高度现代化的"系统/物品/知识"直接植入古代或低科技世界，制造强烈认知冲突和黑色幽默。直播带货穿古、现代超市开在宫廷、APP在修真界——反常识金手指，制造极强喜剧感与爽感。单集爽点密集，特别适合短视频传播。',
    coreGimmick: '现代物品/APP/职业/系统直接降临低科技世界，认知冲撞',
    typicalSettings: ['古代宫廷', '修真界', '末世', '原始部落', '中世纪'],
    audienceTags: ['校园学生', '都市职场', '下沉甜宠'],
    emotionTags: ['爽', '搞笑', '治愈'],
    exampleTitles: ['古代开超市', '修真界滴滴打剑', '古代直播带货', '法医在古代'],
    hotPoints: ['认知冲撞', '降维打击', '反常识笑点', '轻松解压'],
  },
  {
    key: '晋江高榜风格',
    label: '晋江高榜风格',
    tagline: '情感细腻×剧情出彩，人设立体不傻白甜',
    description:
      '晋江文学城高榜风格：人设立体真实（女主不傻白甜，男主不无脑宠），感情线慢热但极度上头，常有反套路桥段和强名场面台词。适合漫剧分镜突出情绪张力，受众粘性高、二创多。',
    coreGimmick: '反套路情感博弈，人设深度驱动剧情',
    typicalSettings: ['现代都市', '古代宅斗', '豪门商战', '职场竞争'],
    audienceTags: ['都市职场', '下沉甜宠', '中年家斗'],
    emotionTags: ['甜', '虐', '爽'],
    exampleTitles: ['重生后渣了权臣', '退婚后太子下乡', '表妹万福'],
    hotPoints: ['反套路', '慢热上头', '名场面台词', '情感拉扯'],
  },
  {
    key: '耽美漫剧',
    label: '耽美漫剧',
    tagline: '双男主主线，强强/互宠/虐恋/穿书救赎',
    description:
      '双男主感情主线，主流风格包括：强强互宠（双向事业+情感）、虐恋/破镜重圆（热度常居前列）、穿书/重生救赎（自带设定反转）。声优选角影响极大，名台词弹幕互动强，情感张力足。',
    coreGimmick: '双男主情感张力，穿书/重生设定反转',
    typicalSettings: ['修真仙界', '现代都市', '古代江湖', '异世界'],
    audienceTags: ['校园学生', '都市职场', '二次元'],
    emotionTags: ['甜', '虐', '爽'],
    exampleTitles: ['二哈和白猫师尊', '人渣反派自救系统', '当年万里觅封侯'],
    hotPoints: ['双向奔赴', '破镜重圆', '强强对决', '穿书反转'],
  },
  {
    key: '穿书系统爽文',
    label: '穿书/系统爽文',
    tagline: '任务式穿越，知道剧情走向、攻略/破坏原著',
    description:
      '主角穿越进书中/游戏中，掌握原著剧情信息，利用"上帝视角"攻略反派、拯救男二、逃离BE结局或炮灰逆袭。强设定代入感快，多周目感剧情，节奏紧凑，适合连更养成。',
    coreGimmick: '知晓原著剧情的信息差，任务系统加持',
    typicalSettings: ['穿入古代宅斗小说', '穿入玄幻修真世界', '穿入都市言情文'],
    audienceTags: ['下沉甜宠', '都市职场', '校园学生'],
    emotionTags: ['爽', '甜', '搞笑'],
    exampleTitles: ['攻略那个反派', '这该死的求生欲', '太子妃咸鱼日常'],
    hotPoints: ['炮灰逆袭', '攻略反派', '拯救男二', '逃出BE'],
  },
  {
    key: '基建科技降维',
    label: '基建/科技/文化降维打击',
    tagline: '不谈恋爱专注攀科技树，搞工业革命建立现代制度',
    description:
      '穿越后不以情感为主线，而是专注攀科技树、搞工业革命、建立现代制度和文化体系。男性向与女性向受众通吃，被称"另类种田爽"。强调"一步步改变世界"的成就感，适合对世界观构建感兴趣的受众。',
    coreGimmick: '系统性构建现代科技/制度/文化体系，缓慢但震撼的文明跃迁',
    typicalSettings: ['古代农业社会', '原始部落', '蒸汽朋克世界', '末世废土'],
    audienceTags: ['逆袭打脸', '战神兵王', '都市重生'],
    emotionTags: ['燃', '爽', '治愈'],
    exampleTitles: ['古代搞科研', '穿到蛮荒搞基建', '美洲日不落'],
    hotPoints: ['科技树攀升', '制度革命', '文明碾压', '建设成就感'],
  },
  {
    key: '中式恐怖',
    label: '中式恐怖',
    tagline: '道诡异仙——熟悉的神鬼体系下埋着不可名状的深渊',
    description:
      '根植于中国本土民间信仰、道教神鬼体系与聊斋志异传统，却以克苏鲁式的"不可知、不可名状"重新构建恐怖内核。与西方恐怖片的"暴露即恐惧"不同，中式恐怖的精髓在于"熟悉感的崩塌"——那些符咒、纸钱、红盖头、停尸房，全都是你见过的东西，却在某一刻对你露出了完全陌生的凶相。\n\n核心张力来自两个维度：①认知错位：人物（和观众）以为自己理解规则，但规则始终比认知深一层；②边界模糊：生与死、神与妖、救与害的界限不断溶解，观众无法确定"善意"是否真的善意。\n\n代表气质：《道诡异仙》的修仙表壳下是逐渐扭曲的精神世界与不可名状的上位存在；《纸嫁衣》以婚俗为入口，让每一个传统仪式都成为恐怖的触发器；《聊斋》新编的鬼怪不再是可被降服的对立面，而是拥有超出人类理解维度的存在逻辑。\n\n漫剧化要点：单集设置一个"规则崩塌时刻"作为钩子；用熟悉的民俗符号建立安全感，再精准打破；恐怖不靠血腥，靠信息不对称和环境异化积累压迫感。',
    coreGimmick: '以本土民俗/道教体系为壳，内藏克苏鲁式宇宙恐怖，熟悉感崩塌是最大杀器',
    typicalSettings: ['农村老宅/祠堂', '道观/阴阳师世界', '民国聊斋异境', '现代都市诡异事件', '阴间/冥界变体'],
    audienceTags: ['悬疑推理', '二次元', '都市职场'],
    emotionTags: ['悬疑', '虐', '燃'],
    exampleTitles: ['道诡异仙', '纸嫁衣', '聊斋新编', '阴阳镇鬼师', '捉妖师手记'],
    hotPoints: ['民俗崩塌', '规则反转', '上位存在降临', '身份真相揭露', '生死边界溶解'],
  },
  {
    key: '规则怪谈',
    label: '规则怪谈',
    tagline: '遵守规则才能活，但规则本身就是陷阱',
    description:
      '以"一套看似明确却暗含致命漏洞的规则"为核心叙事引擎，浸染浓烈的中式民俗底色。主角被困于某个异常空间、诡异职业或超自然处境中，必须通过理解、遵守、破解这套规则来求生——而规则的每一条细读都藏着新的恐怖信息，遵守它可能比违反它更危险。\n\n【中式底色】规则的语言质感来自本土民间禁忌：不是"禁止入内"，而是"逢单日不得开西厢的门"；不是"危险"，而是"若听见有人叫你的名字，不要答应第三声"。这些规则自带中国人熟悉的民俗逻辑，第一遍读觉得有道理，细想却越来越不对——它的背后有一套你不知道的、早已运行许久的规则，而你只是刚刚进入它的射程。\n\n【死亡的处理方式】违规的后果从不正面描写：上一个值班的人"没来交班"，桌上的茶还是热的；公告栏里某个名字被安静地划掉；有人问"3号房的住客呢"，前台只说"退房了"。消失比死亡更令人不安，因为你无法确认它是否真的结束了。\n\n核心爽感循环：获得规则 → 推理规则背后的民俗/鬼神逻辑 → 用规则漏洞破局 → 发现更深一层的元规则 → 循环升级。每一轮反转都是独立的钩子，天然适合短剧的单集完整弧度。\n\n代表气质：《纸嫁衣》的婚俗禁忌链条、国内规则怪谈短文学的极简叙事张力，叠加SCP档案体的冷静压迫感。漫剧化后规则以字幕/旁白形式强调，弹幕猜测规则含义、二创续写新规则的互动势能极强。',
    coreGimmick: '中式民俗禁忌语言包裹的致命规则——规则即世界观，漏洞即生机，消失比死亡更恐怖',
    typicalSettings: ['带奇怪规定的高危职业（守夜人/殡仪馆学徒/古宅看守）', '有禁忌的封闭村庄/孤岛', '表面正常却暗流涌动的租房/酒店', '游戏/副本世界（规则由神明制定）', '某个诡异的"第一天上班"'],
    audienceTags: ['悬疑推理', '二次元', '校园学生'],
    emotionTags: ['悬疑', '爽', '燃'],
    exampleTitles: ['殡仪馆守夜须知', '纸嫁衣', '封村第七天', '这份工作有点怪', '禁忌清单'],
    hotPoints: ['民俗禁忌规则', '消失的人', '元规则反转', '求生破局', '规则背后的真相'],
  },
  {
    key: '都市暗网',
    label: '都市暗网',
    tagline: '日常表皮下藏着一张看不见的网——你以为的自由意志，不过是被允许的选项',
    description:
      '以现代都市为背景，核心设定是"存在一个隐藏在日常生活表层之下的暗网/系统/组织"——它可能是手机里突然出现的神秘APP、无法退出的线上契约、操控命运的上帝游戏、深网中的致命交易平台、或任何以数字/规则/契约形态入侵现实的超自然力量。\n\n与"规则怪谈"的区别：规则怪谈的规则是空间性的（被困在某个异常场所）；都市暗网的规则是网络性的——它无处不在、随时在线、通过你最依赖的设备（手机/电脑/社交媒体）渗透进来，你在自己家里、在地铁上、在公司里都不安全，因为"网"已经覆盖了你的整个生活。\n\n核心恐怖来源：①数字入侵——恐怖不是来自荒郊野岭，而是来自你每天使用的屏幕、APP、聊天记录、外卖订单；②契约陷阱——一旦点击"同意"就无法退出，而条款的真实含义远比字面恐怖；③上帝视角——有不可见的意志在观察和操控，主角的每一个选择都在被评估和利用；④社会实验——普通人被放入精心设计的极端情境，人性在压力下暴露最丑陋的一面。\n\n典型叙事结构：主角偶然接触到"暗网"（下载了一个APP/收到一条陌生链接/签了一份电子协议）→ 获得短期利益（愿望实现/金钱/能力）→ 发现代价远超想象 → 试图退出但发现已被深度绑定 → 被迫完成越来越离谱的"任务" → 最终发现自己只是更大游戏中的一枚棋子。',
    coreGimmick: '数字时代的浮士德契约——屏幕是入口，算法是牢笼，你的欲望就是它的燃料',
    typicalSettings: ['神秘APP/小程序突然出现在手机里', '无法退出的线上契约/用户协议', '暗网交易平台/欲望清单', '社交媒体背后的操控系统', '日常设备（外卖/打车/直播）的异化', '密室/副本式的线下任务空间'],
    audienceTags: ['悬疑推理', '都市职场', '校园学生'],
    emotionTags: ['悬疑', '爽', '燃'],
    exampleTitles: ['致命契约', '屏幕背后', '上帝游戏', '任务清单', '404房间', '欲望清单', '都市暗网'],
    hotPoints: ['数字入侵日常', '契约无法退出', '欲望的代价', '幕后操控者揭露', '人性极限测试', '算法囚笼'],
  },
  {
    key: '冷感生存日志',
    label: '冷感生存日志',
    tagline: '末世之后，我以第一人称冷静地活着，看着世界一点点不对劲',
    description:
      '第一人称视角的冷感末世生存。世界已经/正在崩坏（瘟疫、丧尸、极端气候灾变、辐射或未知灾难），主角是末世里求生的幸存者，全程用第一人称、冷静克制的语气讲述自己如何活下去、以及这个世界如何一点点不对劲。"冷感"是命脉——冷静、下沉、克制，全程第一人称内心独白，对话极少。\n\n注意：题材名里的"日志"只是对这种第一人称、随时间缓慢推进气质的比喻，不是要写成日记体、也不是要在剧情里出现日志/读数/维修单这类文书——就按正常剧情来写，只是语气冷、视角始终是"我"。\n\n张力与恐惧主要来自"认知失调"：本以为掌握的规律被打破、求生常识开始失效、出现一个微小却无法解释的反常。主角有谨慎的求生性格，遇到反常先观察、先判断、按自己的章法处理——可越按章法走，越发现这个世界已经不讲原来的道理了。开局不固定：既可能一开场就已身处成熟末世，也可能从正常生活一步步滑向崩坏（每次随机），不会总是"灾变后第N个月"。\n\n推进以第一人称求生处境的缓慢递进为主，同时嵌入末世求生的现实压力（物资告急、庇护所失效、外部联络中断、感染体或不明存在逼近、其他幸存者带来的危机）；情绪允许爆发但整体始终冷静克制，爆发是被克制反衬出来的一个尖点而非节奏。每一场不留炸裂钩子，只留一个"细想才觉得不对"的冷悬念。\n\n可选（不强制）：具体可验证的求生操作（焊门、封窗、清点物资）带来的真实质感、主角的职业背景。核心始终是第一人称、末世求生、冷静克制。',
    coreGimmick: '第一人称视角的冷感末世生存——冷静克制地讲述灾变后求生，张力源于认知失调而非跳脸惊吓',
    typicalSettings: ['末日避难所/封闭安全屋', '灾变后的城市废墟/无人街区', '废土上的独自迁移与搜寻', '被围困的电站/观测站等据点', '极寒或极热灾变下的孤立处所', '灾后断联的偏远站点'],
    audienceTags: ['末世生存', '悬疑推理', '氛围沉浸'],
    emotionTags: ['悬疑'],
    exampleTitles: ['我在末世', '灾变之后', '世界静了', '活下去的人', '废土独行', '最后一个清醒的人'],
    hotPoints: ['末世生存', '第一人称叙事', '认知失调', '冷悬念', '孤独求生', '规律失效'],
  },
  {
    key: '宇宙进化爽文',
    label: '宇宙进化爽文',
    tagline: '地球危机×星际文明×生命跃迁，普通人一路进化守护家园',
    description:
      '面向类似星际修炼、宇宙文明、异兽危机、生命层级跃迁的男频爽文题材。核心不是照搬具体 IP，而是保留“弱小文明面对宇宙尺度威胁，主角通过天赋、训练、战斗和资源争夺不断进化”的类型快感。适合从地球危机、殖民星球、轨道城市、异兽生态、星际公司、联邦军校等入口展开，强调成长阶梯、战斗爽点、文明守护、家族责任和更高维秩序压迫。',
    coreGimmick: '生命层级进化与宇宙文明压迫，主角从局部危机一路升级到文明级博弈',
    typicalSettings: ['近未来地球', '轨道城市', '殖民星球', '星际军校', '宇宙采矿公司', '异兽生态隔离区'],
    audienceTags: ['男频', '二次元', '末世生存'],
    emotionTags: ['燃', '爽', '悬疑'],
    exampleTitles: ['星域觉醒', '异兽纪元', '轨道武者', '文明边境', '吞星之后'],
    hotPoints: ['天赋觉醒', '异兽猎杀', '等级跃迁', '文明守护', '星际争霸', '强者试炼'],
  },
];

export const GENRE_MAP = new Map<Genre, GenreMeta>(GENRE_META.map((g) => [g.key, g]));

export const AUDIENCES = ['女频', '男频'] as const;

export type Audience = (typeof AUDIENCES)[number];

export const POLICY_RISK = ['none', 'yellow', 'red'] as const;
export type PolicyRisk = (typeof POLICY_RISK)[number];

export const CORE_EMOTIONS = ['爽', '甜', '虐', '燃', '治愈', '搞笑', '解气', '悬疑'] as const;
export type CoreEmotion = (typeof CORE_EMOTIONS)[number];

/** 以第一人称叙事生成、且无需 AI 润色的题材（与后端 taxonomy.py 的 FIRST_PERSON_SCRIPT_GENRES 对齐）。 */
export const FIRST_PERSON_SCRIPT_GENRES = ['冷感生存日志'] as const;

export function isFirstPersonScriptGenre(genreTags: readonly string[] | null | undefined): boolean {
  return (genreTags ?? []).some((g) => (FIRST_PERSON_SCRIPT_GENRES as readonly string[]).includes(g));
}

/**
 * 红果模式题材分级（与后端 taxonomy.py 对齐）。
 * A 级(standard) 自动标准红果(台词95%+)，B 级(light) 轻量红果(85-92%)，C 级(forbidden) 禁用红果。
 */
export type HongguoLevel = 'standard' | 'light' | 'forbidden';

export const HONGGUO_AUTO_GENRES = [
  '红果热门',
  '传统穿越爽文',
  '维度差穿越',
  '晋江高榜风格',
  '耽美漫剧',
  '穿书系统爽文',
] as const;

export const HONGGUO_LIGHT_GENRES = ['基建科技降维', '都市暗网', '规则怪谈', '宇宙进化爽文'] as const;

export const HONGGUO_FORBIDDEN_GENRES = ['中式恐怖', '冷感生存日志'] as const;

const HONGGUO_ALIAS_AUTO = [
  '红果风格',
  '极速对白',
  '台词密集',
  '快节奏',
  '对话剧',
  '霸总',
  '复仇',
  '逆袭',
  '打脸',
  '甜宠',
  '虐恋',
  '重生',
  '穿越',
  '豪门',
  '都市情感',
];
const HONGGUO_ALIAS_FORBIDDEN = ['恐怖', '末世生存', '悬疑氛围'];

/**
 * 根据题材标签解析红果模式等级。优先级（安全优先）：forbidden > standard > light。
 * 返回 null 表示未命中任何已知题材，调用方按默认标准模式处理。
 */
export function resolveHongguoLevel(
  genreTags: readonly string[] | null | undefined,
): HongguoLevel | null {
  const tags = genreTags ?? [];
  if (tags.length === 0) return null;

  if (tags.some((g) => (HONGGUO_FORBIDDEN_GENRES as readonly string[]).includes(g))) return 'forbidden';
  if (tags.some((g) => HONGGUO_ALIAS_FORBIDDEN.some((kw) => g.includes(kw)))) return 'forbidden';

  if (tags.some((g) => (HONGGUO_AUTO_GENRES as readonly string[]).includes(g))) return 'standard';
  if (tags.some((g) => HONGGUO_ALIAS_AUTO.some((alias) => g.includes(alias)))) return 'standard';

  if (tags.some((g) => (HONGGUO_LIGHT_GENRES as readonly string[]).includes(g))) return 'light';

  return null;
}

/** 用于 UI 排序选项 */
export const RADAR_SORTS = ['suit', 'heat', 'hook', 'time'] as const;
export type RadarSort = (typeof RADAR_SORTS)[number];
export const RADAR_SORT_LABELS: Record<RadarSort, string> = {
  suit: '适配度↓',
  heat: '热度↓',
  hook: '钩子密度↓',
  time: '热点时间↓',
};

// ──────────────────────────────────────────────────────────────────
// 影视风格注册表(前端统一数据源 · 与后端 video_style_registry.py 对齐)
// ──────────────────────────────────────────────────────────────────

export interface VideoStyleMeta {
  name: string;
  tagline: string;
  description: string;
  suitableFor: string[];
}

/** @deprecated 使用 useVideoStyles() hook 从 GET /api/v1/styles 获取 */
export const VIDEO_STYLES: VideoStyleMeta[] = [];
/** @deprecated 使用 useVideoStyles() hook */
export const VIDEO_STYLE_NAMES: string[] = [];
